import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

// Components
import { ContentContainer } from '../../components/Layout/ContentContainer';
import Logo from '../../components/Logo';
import MenuOpener from '../../components/MenuOpener';
import TextBlock from '../../components/TextBlock';
import Title, { Subtext } from '../../components/Title';
import Footer from '../../components/Layout/Footer';
import SealAnimation from '../../components/SealAnimation';

// Assets
import { brandColors, device } from '../../styles/variables';
import blueLogo from '../../assets/images/logos/rd-logo-dark.png';
import Hyperlink from '../../components/Hyperlink';

const Wrapper = styled.div`
  background: ${brandColors.white};
`;

const ContentWrapper = styled.div`
  padding: 0 0 150px 0;
`;

const TitleContainer = styled.div`
  position: relative;
  margin: 154px 0 0 0;
`;

const SealContainer = styled.div`
  margin-top: 28px;

  @media ${device.tablet} {
    margin: 0;
    position: absolute;
    right: 100px;
    top: 0;
  }
`;

const Cookies = () => (
  <Wrapper>
    <MenuOpener className="menu-opener" />
    <Logo logo={blueLogo} />
    <ContentWrapper>
      <ContentContainer>
        <TitleContainer>
          <Title subtitle="geeky legals" color={brandColors.blue}>
            Cookies
            <SealContainer>
              <SealAnimation />
            </SealContainer>
            <Subtext width="636px">
              We are committed to data security and the fair and transparent processing of personal
              data.
            </Subtext>
          </Title>
        </TitleContainer>
        <TextBlock width="590px">
          <p>__</p>
          <p>
            Cookies consist of portions of code installed in the browser that assist the Owner in
            providing the Service according to the purposes described. Some of the purposes for
            which Cookies are installed may also require the User&apos;s consent.
          </p>
          <p>
            Where the installation of Cookies is based on consent, such consent can be freely
            withdrawn at any time following the instructions provided in this document.
          </p>

          <h2>Technical Cookies and Cookies serving aggregated statistical purposes</h2>

          <h3>Activity strictly necessary for the functioning of the Service</h3>
          <p>
            Radical Departures Ltd. uses Cookies to save the User&apos;s session and to carry out
            other activities that are strictly necessary for the operation of Radical Departures
            Ltd., for example in relation to the distribution of traffic.
          </p>

          <h3>Activity regarding the saving of preferences, optimization, and statistics</h3>
          <p>
            Radical Departures Ltd. uses Cookies to save browsing preferences and to optimise the
            User&apos;s browsing experience. Among these Cookies are, for example, those used for
            the setting of language and currency preferences or for the management of first party
            statistics employed directly by the Owner of the site.
          </p>

          <p>--</p>

          <h2>Other types of Cookies or third parties that install Cookies</h2>
          <p>
            Some of the services listed below collect statistics in an anonymised and aggregated
            form and may not require the consent of the User or may be managed directly by the Owner
            - depending on how they are described - without the help of third parties.
          </p>
          <p>
            If any third party operated services are listed among the tools below, these may be used
            to track Users’ browsing habits – in addition to the information specified herein and
            without the Owner’s knowledge. Please refer to the privacy policy of the listed services
            for detailed information.
          </p>

          <p>--</p>

          <h2>Analytics</h2>

          <h3>Google Analytics (Google Ireland Limited)</h3>
          <p>
            Google Analytics is a web analysis service provided by Google Ireland Limited
            (“Google”). Google utilises the Data collected to track and examine the use of Radical
            Departures Ltd., to prepare reports on its activities and share them with other Google
            services.
          </p>
          <p>
            Google may use the Data collected to contextualise and personalise the ads of its own
            advertising network.
          </p>
          <p>Personal Data collected: Cookies and Usage Data.</p>
          <p>
            Place of processing: United States –{' '}
            <Hyperlink
              linkAddress="https://policies.google.com/privacy?hl=en"
              linkName="Privacy Policy"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            –{' '}
            <Hyperlink
              linkAddress="https://tools.google.com/dlpage/gaoptout?hl=en"
              linkName="Opt Out"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            . Privacy Shield participant.
          </p>

          <p>--</p>

          <h2>How to provide or withdraw consent to the installation of Cookies</h2>
          <p>
            In addition to what is specified in this document, the User can manage preferences for
            Cookies directly from within their own browser and prevent – for example – third parties
            from installing Cookies.
          </p>
          <p>
            Through browser preferences, it is also possible to delete Cookies installed in the
            past, including the Cookies that may have saved the initial consent for the installation
            of Cookies by this website.
          </p>
          <p>
            Users can, for example, find information about how to manage Cookies in the most
            commonly used browsers at the following addresses:{' '}
            <Hyperlink
              linkAddress="https://support.google.com/chrome/answer/95647?hl=en&p=cpn_cookies"
              linkName="Google Chrome"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />,{' '}
            <Hyperlink
              linkAddress="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
              linkName="Mozilla Firefox"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />,{' '}
            <Hyperlink
              linkAddress="https://support.apple.com/kb/PH21411?locale=en_US"
              linkName="Apple Safari"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />,{' '}
            <Hyperlink
              linkAddress="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies"
              linkName="Microsoft Internet Explorer"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />.
          </p>
          <p>
            With regard to Cookies installed by third parties, Users can manage their preferences
            and withdrawal of their consent by clicking the related opt-out link (if provided), by
            using the means provided in the third party&apos;s privacy policy, or by contacting the
            third party.
          </p>
          <p>
            Notwithstanding the above, the Owner informs that Users may follow the instructions
            provided on the subsequently linked initiatives by the{' '}
            <Hyperlink
              linkAddress="http://www.youronlinechoices.eu/"
              linkName="EDAA"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            (EU), the{' '}
            <Hyperlink
              linkAddress="https://www.networkadvertising.org/understanding-digital-advertising"
              linkName="Network Advertising Initiative"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            (US) and the{' '}
            <Hyperlink
              linkAddress="https://www.aboutads.info/consumers/"
              linkName="Digital Advertising Alliance"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            (US),{' '}
            <Hyperlink
              linkAddress="https://youradchoices.ca/understanding-online-advertising/"
              linkName="DAAC"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            (Canada),{' '}
            <Hyperlink
              linkAddress="http://www.ddai.info/optout"
              linkName="DDAI"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            (Japan) or other similar services. Such initiatives allow Users to select their tracking
            preferences for most of the advertising tools. The Owner thus recommends that Users make
            use of these resources in addition to the information provided in this document.
          </p>

          <p>--</p>

          <h2>Owner and Data Controller</h2>
          <h3>Radical Departures Ltd.</h3>
          <p>
            The Printworks, 7 Blake Mews,<br />Kew, London, TW9 3GA<br />
            <Hyperlink linkName="020&nbsp;8334&nbsp;7860" linkAddress="tel:02083347860" />
          </p>
          <p>
            Since the installation of third-party Cookies and other tracking systems through the
            services used within Radical Departures Ltd. cannot be technically controlled by the
            Owner, any specific references to Cookies and tracking systems installed by third
            parties are to be considered indicative. In order to obtain complete information, the
            User is kindly requested to consult the privacy policy for the respective third-party
            services listed in this document.
          </p>
          <p>
            Given the objective complexity surrounding the identification of technologies based on
            Cookies, Users are encouraged to contact the Owner should they wish to receive any
            further information on the use of Cookies by Radical Departures Ltd.
          </p>

          <p>--</p>

          <h2>Definitions and legal references</h2>
          <h3>Personal Data (or Data)</h3>
          <p>
            Any information that directly, indirectly, or in connection with other information —
            including a personal identification number — allows for the identification or
            identifiability of a natural person.
          </p>

          <h3>Usage Data</h3>
          <p>
            Information collected automatically through Radical Departures Ltd. (or third-party
            services employed in Radical Departures Ltd.), which can include: the IP addresses or
            domain names of the computers utilised by the Users who use Radical Departures Ltd., the
            URI addresses (Uniform Resource Identifier), the time of the request, the method
            utilised to submit the request to the server, the size of the file received in response,
            the numerical code indicating the status of the server&apos;s answer (successful
            outcome, error, etc.), the country of origin, the features of the browser and the
            operating system utilised by the User, the various time details per visit (e.g., the
            time spent on each page within the Application) and the details about the path followed
            within the Application with special reference to the sequence of pages visited, and
            other parameters about the device operating system and/or the User&apos;s IT
            environment.
          </p>

          <h3>User</h3>
          <p>
            The individual using Radical Departures Ltd. who, unless otherwise specified, coincides
            with the Data Subject.
          </p>

          <h3>Data Subject</h3>
          <p>The natural person to whom the Personal Data refers.</p>

          <h3>Data Processor (or Data Supervisor)</h3>
          <p>
            The natural or legal person, public authority, agency or other body which processes
            Personal Data on behalf of the Controller, as described in this privacy policy.
          </p>

          <h3>Data Controller (or Owner)</h3>
          <p>
            The natural or legal person, public authority, agency or other body which, alone or
            jointly with others, determines the purposes and means of the processing of Personal
            Data, including the security measures concerning the operation and use of Radical
            Departures Ltd. The Data Controller, unless otherwise specified, is the Owner of Radical
            Departures Ltd.
          </p>

          <h3>Radical Departures Ltd. (or this Application)</h3>
          <p>The means by which the Personal Data of the User is collected and processed.</p>

          <h3>Service</h3>
          <p>
            The service provided by Radical Departures Ltd. as described in the relative terms (if
            available) and on this site/application.
          </p>

          <h3>European Union (or EU)</h3>
          <p>
            Unless otherwise specified, all references made within this document to the European
            Union include all current member states to the European Union and the European Economic
            Area.
          </p>

          <h3>Cookies</h3>
          <p>Small sets of data stored in the User&apos;s device.</p>

          <p>--</p>

          <h2>Legal information</h2>
          <p>
            This privacy statement has been prepared based on provisions of multiple legislations,
            including Art. 13/14 of Regulation (EU) 2016/679 (General Data Protection Regulation).
          </p>
          <p>
            This privacy policy relates solely to Radical Departures Ltd., if not stated otherwise
            within this document.
          </p>
        </TextBlock>
      </ContentContainer>
    </ContentWrapper>
    <Footer />
  </Wrapper>
);

export default connect()(Cookies);



// WEBPACK FOOTER //
// ./src/pages/Cookies/index.jsx